import React from 'react';

import { CaretUpOutlined, CaretDownOutlined, MinusOutlined } from '@ant-design/icons';
import { Row, Col, Card, Typography, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

const formatValue = (value, precision, prefix = '', suffix = '') => {
  const num = Number(value) || 0;
  const formatted = precision > 0 ? num.toLocaleString(undefined, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }) : num.toLocaleString();
  return `${prefix}${formatted}${suffix}`;
};

const KpiCards = ({ data }) => {
  const { t } = useTranslation();

  if (!data || !data.current) {
    return null;
  }

  const { current, previous, comparison, metadata } = data;
  const cs = metadata?.currency_symbol || '$';
  const dp = metadata?.currency_decimal_places ?? 2;

  const kpiItems = [
    {
      key: 'net_revenue',
      title: t('shared.analytics.kpi.net_revenue'),
      value: current.net_revenue ?? current.total_revenue ?? 0,
      previousValue: previous?.net_revenue ?? previous?.total_revenue ?? 0,
      change: comparison?.net_revenue_change ?? comparison?.revenue_change,
      precision: dp,
      prefix: cs,
      hint: current.gross_revenue != null
        ? `${t('shared.analytics.kpi.gross_revenue_hint')}: ${cs}${(current.gross_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`
        : null,
    },
    {
      key: 'orders',
      title: t('shared.analytics.kpi.total_orders'),
      value: current.total_orders || 0,
      previousValue: previous?.total_orders || 0,
      change: comparison?.orders_change,
      precision: 0,
    },
    {
      key: 'aov',
      title: t('shared.analytics.kpi.avg_order_value'),
      value: current.avg_order_value || 0,
      previousValue: previous?.avg_order_value || 0,
      change: comparison?.aov_change,
      precision: dp,
      prefix: cs,
    },
    {
      key: 'return_rate',
      title: t('shared.analytics.kpi.return_rate'),
      value: current.return_rate ?? 0,
      previousValue: previous?.return_rate ?? 0,
      change: comparison?.return_rate_change,
      precision: 1,
      suffix: '%',
      invertColor: true,
    },
  ];

  const renderChange = (change, invertColor) => {
    if (change === undefined || change === null) return null;

    if (change === 0) {
      return (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 2,
          fontSize: 12, color: 'rgba(0,0,0,0.45)',
          background: '#fafafa', borderRadius: 4, padding: '2px 8px',
        }}>
          <MinusOutlined style={{ fontSize: 10 }} /> 0%
        </span>
      );
    }

    const isPositive = change > 0;
    const color = invertColor
      ? (isPositive ? '#ff4d4f' : '#52c41a')
      : (isPositive ? '#52c41a' : '#ff4d4f');
    const bg = invertColor
      ? (isPositive ? '#fff1f0' : '#f6ffed')
      : (isPositive ? '#f6ffed' : '#fff1f0');

    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 2,
        fontSize: 12, fontWeight: 500, color,
        background: bg, borderRadius: 4, padding: '2px 8px',
      }}>
        {isPositive ? <CaretUpOutlined style={{ fontSize: 10 }} /> : <CaretDownOutlined style={{ fontSize: 10 }} />}
        {Math.abs(change).toFixed(1)}%
      </span>
    );
  };

  return (
    <Row gutter={[16, 16]}>
      {kpiItems.map((item) => (
        <Col key={item.key} xs={24} sm={12} lg={6} style={{ display: 'flex' }}>
          <Card bordered={false} styles={{ body: { padding: '20px 24px', display: 'flex', flexDirection: 'column', height: '100%' } }} style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text type="secondary" style={{ fontSize: 13 }}>{item.title}</Text>
              {item.change !== undefined && item.change !== null && (
                <Tooltip title={`${t('shared.analytics.kpi.previous_period')}: ${formatValue(item.previousValue, item.precision, item.prefix, item.suffix)}`}>
                  {renderChange(item.change, item.invertColor)}
                </Tooltip>
              )}
            </div>

            <div style={{ marginTop: 12, flex: 1, display: 'flex', alignItems: 'center' }}>
              <span style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.5px', color: 'rgba(0,0,0,0.88)' }}>
                {formatValue(item.value, item.precision, item.prefix, item.suffix)}
              </span>
            </div>

            <div style={{ marginTop: 8, minHeight: 18 }}>
              {item.hint ? (
                <Text type="secondary" style={{ fontSize: 12 }}>{item.hint}</Text>
              ) : null}
            </div>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default KpiCards;
